/**
 * Country / nationality reference list (Statistics & Analytics Platform, Step 0.3).
 *
 * Used by the optional "nationality" field at sign-up and in account settings.
 * `code` is ISO 3166-1 alpha-2 (uppercase) — the value stored on User.nationality
 * and aggregated by the audience analytics. Bilingual labels for the RTL UI.
 *
 * Broad but Saudi-market-focused (GCC, MENA, the major expatriate origins, plus
 * the main Western/Asian countries). Add entries here as needed — the backend
 * accepts any 2-letter code, so the list is purely the UI's pick-list.
 */
export interface Country {
  code: string;
  en: string;
  ar: string;
}

export const COUNTRIES: Country[] = [
  // GCC
  { code: 'SA', en: 'Saudi Arabia', ar: 'السعودية' },
  { code: 'AE', en: 'United Arab Emirates', ar: 'الإمارات' },
  { code: 'KW', en: 'Kuwait', ar: 'الكويت' },
  { code: 'QA', en: 'Qatar', ar: 'قطر' },
  { code: 'BH', en: 'Bahrain', ar: 'البحرين' },
  { code: 'OM', en: 'Oman', ar: 'عُمان' },
  // MENA
  { code: 'EG', en: 'Egypt', ar: 'مصر' },
  { code: 'JO', en: 'Jordan', ar: 'الأردن' },
  { code: 'LB', en: 'Lebanon', ar: 'لبنان' },
  { code: 'SY', en: 'Syria', ar: 'سوريا' },
  { code: 'IQ', en: 'Iraq', ar: 'العراق' },
  { code: 'YE', en: 'Yemen', ar: 'اليمن' },
  { code: 'PS', en: 'Palestine', ar: 'فلسطين' },
  { code: 'SD', en: 'Sudan', ar: 'السودان' },
  { code: 'LY', en: 'Libya', ar: 'ليبيا' },
  { code: 'TN', en: 'Tunisia', ar: 'تونس' },
  { code: 'DZ', en: 'Algeria', ar: 'الجزائر' },
  { code: 'MA', en: 'Morocco', ar: 'المغرب' },
  { code: 'MR', en: 'Mauritania', ar: 'موريتانيا' },
  { code: 'SO', en: 'Somalia', ar: 'الصومال' },
  { code: 'DJ', en: 'Djibouti', ar: 'جيبوتي' },
  { code: 'KM', en: 'Comoros', ar: 'جزر القمر' },
  // South / South-East Asia (major expatriate origins)
  { code: 'IN', en: 'India', ar: 'الهند' },
  { code: 'PK', en: 'Pakistan', ar: 'باكستان' },
  { code: 'BD', en: 'Bangladesh', ar: 'بنغلاديش' },
  { code: 'PH', en: 'Philippines', ar: 'الفلبين' },
  { code: 'ID', en: 'Indonesia', ar: 'إندونيسيا' },
  { code: 'NP', en: 'Nepal', ar: 'نيبال' },
  { code: 'LK', en: 'Sri Lanka', ar: 'سريلانكا' },
  { code: 'MY', en: 'Malaysia', ar: 'ماليزيا' },
  { code: 'AF', en: 'Afghanistan', ar: 'أفغانستان' },
  { code: 'CN', en: 'China', ar: 'الصين' },
  { code: 'JP', en: 'Japan', ar: 'اليابان' },
  { code: 'KR', en: 'South Korea', ar: 'كوريا الجنوبية' },
  { code: 'TR', en: 'Türkiye', ar: 'تركيا' },
  { code: 'IR', en: 'Iran', ar: 'إيران' },
  // Africa
  { code: 'ET', en: 'Ethiopia', ar: 'إثيوبيا' },
  { code: 'ER', en: 'Eritrea', ar: 'إريتريا' },
  { code: 'KE', en: 'Kenya', ar: 'كينيا' },
  { code: 'NG', en: 'Nigeria', ar: 'نيجيريا' },
  { code: 'GH', en: 'Ghana', ar: 'غانا' },
  { code: 'UG', en: 'Uganda', ar: 'أوغندا' },
  { code: 'TZ', en: 'Tanzania', ar: 'تنزانيا' },
  { code: 'ZA', en: 'South Africa', ar: 'جنوب أفريقيا' },
  // Europe
  { code: 'GB', en: 'United Kingdom', ar: 'المملكة المتحدة' },
  { code: 'FR', en: 'France', ar: 'فرنسا' },
  { code: 'DE', en: 'Germany', ar: 'ألمانيا' },
  { code: 'IT', en: 'Italy', ar: 'إيطاليا' },
  { code: 'ES', en: 'Spain', ar: 'إسبانيا' },
  { code: 'NL', en: 'Netherlands', ar: 'هولندا' },
  { code: 'SE', en: 'Sweden', ar: 'السويد' },
  { code: 'RU', en: 'Russia', ar: 'روسيا' },
  { code: 'UA', en: 'Ukraine', ar: 'أوكرانيا' },
  // Americas / Oceania
  { code: 'US', en: 'United States', ar: 'الولايات المتحدة' },
  { code: 'CA', en: 'Canada', ar: 'كندا' },
  { code: 'BR', en: 'Brazil', ar: 'البرازيل' },
  { code: 'AU', en: 'Australia', ar: 'أستراليا' },
];

export default COUNTRIES;
